/* Added by MORYARTY (MOD-041) — collection "Load more" with prefetch + count.

   Replaces Ajaxinate on the collection product grid with a small loader that:
     1. PREFETCHES the next page in the background as the Load More button
        approaches the viewport (IntersectionObserver, ~1200px ahead), and again
        right after each load — so clicking Load More appends instantly.
     2. Appends the next page's <li> items into the EXISTING #product-grid (one
        continuous grid, correct alignment) instead of stacking new <ul>s.
     3. Updates a translatable "X / Y items" count.

   Graceful fallback: the pagination's <a href> is a real next-page link, so if
   JS fails the click just navigates (classic pagination). Re-inits on
   shopify:section:load for the theme editor. Idempotent (guards via dataset).

   Scoped to the collection grid only — search uses classic numbered pagination.
*/
(function () {
  'use strict';

  var activeIO = null; // MOD-132 — prefetch observer of the currently bound button

  function init(root) {
    var scope = root && root.querySelector ? root : document;
    var pagination = scope.querySelector('#AjaxinatePagination');
    var grid = document.querySelector('#product-grid');
    if (!pagination || !grid) return;
    if (pagination.dataset.mpBound === '1') return; // already wired
    pagination.dataset.mpBound = '1';

    var link = pagination.querySelector('a');
    var countEl = pagination.querySelector('[data-load-count]');
    var total = countEl ? parseInt(countEl.getAttribute('data-total'), 10) || 0 : 0;
    var tpl = countEl ? countEl.getAttribute('data-count-template') || '' : '';

    var nextUrl = link ? link.href : null;
    var prefetchPromise = null;
    var prefetchedFor = null;
    var loading = false;

    function productCount() {
      return grid.querySelectorAll('.grid__item:not(.moryarty-content-card)').length;
    }

    function updateCount() {
      if (!countEl || !tpl) return;
      var loaded = nextUrl ? productCount() : total; // no next page → everything loaded
      countEl.textContent = tpl.replace('[loaded]', loaded).replace('[total]', total);
    }

    function parse(text) {
      var doc = new DOMParser().parseFromString(text, 'text/html');
      var g = doc.querySelector('#product-grid');
      var a = doc.querySelector('#AjaxinatePagination a');
      return { itemsHtml: g ? g.innerHTML : '', nextUrl: a ? a.href : null };
    }

    /* MOD-097 — append with content-card dedup. The auto content cards' order is
       seeded per render DATE (daily rotation); a Load-more fetch that straddles
       midnight (or a page-cache boundary) can re-serve a card already in the
       grid. Cards carry data-mcc-handle on their <li>; drop incoming duplicates
       before appending. Product <li>s are untouched. */
    function appendItems(html) {
      var tpl = document.createElement('template');
      tpl.innerHTML = html;
      var incoming = tpl.content.querySelectorAll('li[data-mcc-handle]');
      for (var i = 0; i < incoming.length; i++) {
        var h = incoming[i].getAttribute('data-mcc-handle');
        if (h && grid.querySelector('li[data-mcc-handle="' + h + '"]')) {
          incoming[i].parentNode.removeChild(incoming[i]);
        }
      }
      grid.appendChild(tpl.content);
    }

    function fetchPage(url) {
      return fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (t) { return t ? parse(t) : null; })
        .catch(function () { return null; });
    }

    // Warm the cache for the current nextUrl (once).
    function prefetch() {
      if (!nextUrl || prefetchedFor === nextUrl) return;
      prefetchedFor = nextUrl;
      prefetchPromise = fetchPage(nextUrl);
    }

    function loadMore() {
      if (loading || !nextUrl) return;
      loading = true;
      if (link) link.classList.add('is-loading');

      var pending = (prefetchedFor === nextUrl && prefetchPromise)
        ? prefetchPromise
        : fetchPage(nextUrl);

      pending.then(function (res) {
        // MOD-132 — a filter/sort re-render replaced this button (and its grid) while the
        // fetch was in flight; the new button has its own instance. Drop the stale result —
        // in particular don't fall back to navigating to the OLD sort's next page.
        if (!pagination.isConnected) return;
        if (!res || !res.itemsHtml) {
          // network/parse failure → fall back to a normal page navigation
          if (nextUrl) { window.location.href = nextUrl; }
          return;
        }
        appendItems(res.itemsHtml); // MOD-097 — was grid.insertAdjacentHTML('beforeend', ...)
        nextUrl = res.nextUrl;
        prefetchPromise = null;
        prefetchedFor = null;
        loading = false;
        if (link) link.classList.remove('is-loading');

        if (!nextUrl) {
          if (link) link.style.display = 'none';
        } else if (link) {
          link.href = nextUrl;
        }
        updateCount();
        prefetch(); // warm the following page immediately
      });
    }

    if (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        loadMore();
      });
    }

    // Prefetch as the button approaches the viewport (the "preload while scrolling" ask).
    if ('IntersectionObserver' in window && pagination) {
      if (activeIO) activeIO.disconnect(); // MOD-132 — the previous (replaced) button's
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting) prefetch(); });
      }, { rootMargin: '1200px 0px' });
      io.observe(pagination);
      activeIO = io;
    } else {
      prefetch(); // no IO support → just warm it once
    }

    updateCount();
  }

  /* MOD-132 — Dawn's facets.js (filter, sort, back/forward between filters) replaces the
     innerHTML of #ProductGridContainer, which brings a new, unbound Load more button and a new
     #product-grid. Without re-binding, the button fell back to its plain link: a full page load
     of ?page=2 (scrolled to the top, page 1 gone, count wrong). Watch the container's direct
     children only — Load more's own appends go into #product-grid, one level down. */
  function watchGrid() {
    var container = document.getElementById('ProductGridContainer');
    if (!container || !('MutationObserver' in window)) return;
    if (container.dataset.mpWatched === '1') return;
    container.dataset.mpWatched = '1';
    new MutationObserver(function () { init(document); }).observe(container, { childList: true });
  }

  function boot() {
    init(document);
    watchGrid(); // MOD-132
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Theme editor: re-wire when the section reloads.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
    watchGrid(); // MOD-132 — the reloaded section brings a new container
  });
})();
